import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Globe, Building, Loader2, CheckCircle, AlertCircle, Users } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Switch from '../../../components/ui/Switch';
import {
  getCompanySettings,
  updateCompanySettings,
  getDepartmentSettings,
  updateDepartmentSetting,
  getPersonaSettings,
  updatePersonaSettings,
  type CompanyProfileSettingsView,
  type DepartmentProfileSettingsView,
  type CompanyProfileSettingsInput,
  type DepartmentProfileSettingsInput,
  type PersonaSettingsView,
  type PersonaSettingsInput,
} from '../../../api/PublicProfile/publicProfileApi';

interface CompanyControlsTabProps {
  selectedPersonaId: string;
  onSettingsChange?: () => void;
}

const CompanyControlsTab: React.FC<CompanyControlsTabProps> = ({ selectedPersonaId, onSettingsChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companySettings, setCompanySettings] = useState<CompanyProfileSettingsView | null>(null);
  const [personaSettings, setPersonaSettings] = useState<PersonaSettingsView | null>(null);
  const [departmentSettings, setDepartmentSettings] = useState<DepartmentProfileSettingsView[]>([]);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  // Load company-wide data (independent of persona)
  const loadCompanyData = useCallback(async () => {
    setLoading(true);
    try {
      const [companyResult, departmentResult] = await Promise.all([
        getCompanySettings(),
        getDepartmentSettings(),
      ]);

      if (companyResult.status === 200 && companyResult.payload) {
        setCompanySettings(companyResult.payload);
      }

      if (departmentResult.status === 200 && departmentResult.payload) {
        setDepartmentSettings(departmentResult.payload);
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
      showToast('error', t('Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load persona-specific data
  const loadPersonaData = useCallback(async () => {
    if (!selectedPersonaId) {
      setPersonaSettings(null);
      return;
    }

    try {
      const personaId = parseInt(selectedPersonaId, 10);
      const personaResult = await getPersonaSettings(personaId);

      if (personaResult.status === 200 && personaResult.payload) {
        setPersonaSettings(personaResult.payload);
      }
    } catch (error) {
      console.error('Error loading persona settings:', error);
    }
  }, [selectedPersonaId]);

  // Load company-wide data on mount
  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  // Reload persona data when persona changes
  useEffect(() => {
    loadPersonaData();
  }, [loadPersonaData]);

  // Handle company-wide toggle (publicProfilesEnabled only)
  const handleCompanyToggle = async (field: keyof CompanyProfileSettingsInput, value: boolean) => {
    if (!companySettings) return;

    const updatedSettings: CompanyProfileSettingsInput = {
      publicProfilesEnabled: companySettings.publicProfilesEnabled,
      allowEmployeeToggle: companySettings.allowEmployeeToggle,
      companyLogoUrl: companySettings.companyLogoUrl,
      primaryColor: companySettings.primaryColor,
      secondaryColor: companySettings.secondaryColor,
      disclaimerText: companySettings.disclaimerText,
      footerText: companySettings.footerText,
      showCompanyLogo: companySettings.showCompanyLogo,
      showDisclaimer: companySettings.showDisclaimer,
      [field]: value,
    };

    // Optimistic update
    setCompanySettings(prev => prev ? { ...prev, [field]: value } : prev);
    setSaving(true);

    try {
      const result = await updateCompanySettings(updatedSettings);
      if (result.status === 200 && result.payload) {
        setCompanySettings(result.payload);
        onSettingsChange?.();
      } else {
        // Revert on failure
        setCompanySettings(prev => prev ? { ...prev, [field]: !value } : prev);
        showToast('error', t('Failed to update setting'));
      }
    } catch (error) {
      // Revert on error
      setCompanySettings(prev => prev ? { ...prev, [field]: !value } : prev);
      console.error('Error updating company setting:', error);
      showToast('error', t('Failed to update setting'));
    } finally {
      setSaving(false);
    }
  };

  // Handle persona-specific toggle (allowEmployeeToggle, requiresApproval)
  const handlePersonaToggle = async (field: 'allowEmployeeToggle' | 'requiresApproval' | 'allowSlugRegeneration', value: boolean) => {
    if (!personaSettings || !selectedPersonaId) return;

    const personaId = parseInt(selectedPersonaId, 10);

    // Optimistic update
    setPersonaSettings(prev => prev ? { ...prev, [field]: value } : prev);
    setSaving(true);

    try {
      const input: PersonaSettingsInput = {
        personaId,
        companyLogoUrl: personaSettings.companyLogoUrl || null,
        primaryColor: personaSettings.primaryColor,
        secondaryColor: personaSettings.secondaryColor,
        showCompanyLogo: personaSettings.showCompanyLogo,
        showDisclaimer: personaSettings.showDisclaimer,
        disclaimerText: personaSettings.disclaimerText || null,
        footerText: personaSettings.footerText || null,
        allowEmployeeToggle: field === 'allowEmployeeToggle' ? value : personaSettings.allowEmployeeToggle,
        requiresApproval: field === 'requiresApproval' ? value : personaSettings.requiresApproval,
        allowSlugRegeneration: field === 'allowSlugRegeneration' ? value : personaSettings.allowSlugRegeneration,
      };

      const result = await updatePersonaSettings(input);
      if (result.status === 200 && result.payload) {
        setPersonaSettings(result.payload);
        onSettingsChange?.();
        showToast('success', t('Persona setting updated'));
      } else {
        // Revert on failure
        setPersonaSettings(prev => prev ? { ...prev, [field]: !value } : prev);
        showToast('error', t('Failed to update setting'));
      }
    } catch (error) {
      // Revert on error
      setPersonaSettings(prev => prev ? { ...prev, [field]: !value } : prev);
      console.error('Error updating persona setting:', error);
      showToast('error', t('Failed to update setting'));
    } finally {
      setSaving(false);
    }
  };

  const handleDepartmentToggle = async (
    departmentId: number,
    field: keyof DepartmentProfileSettingsInput,
    value: boolean
  ) => {
    const dept = departmentSettings.find(d => d.departmentId === departmentId);
    if (!dept) return;

    const input: DepartmentProfileSettingsInput = {
      profilesEnabled: field === 'profilesEnabled' ? value : dept.profilesEnabled,
      requiresApproval: field === 'requiresApproval' ? value : dept.requiresApproval,
    };

    // Optimistic update
    setDepartmentSettings(prev =>
      prev.map(d => d.departmentId === departmentId ? { ...d, [field]: value } : d)
    );

    try {
      const result = await updateDepartmentSetting(departmentId, input);
      if (result.status === 200 && result.payload) {
        setDepartmentSettings(prev =>
          prev.map(d => d.departmentId === departmentId ? result.payload! : d)
        );
        onSettingsChange?.();
      } else {
        // Revert on failure
        setDepartmentSettings(prev =>
          prev.map(d => d.departmentId === departmentId ? { ...d, [field]: !value } : d)
        );
        showToast('error', t('Failed to update department setting'));
      }
    } catch (error) {
      // Revert on error
      setDepartmentSettings(prev =>
        prev.map(d => d.departmentId === departmentId ? { ...d, [field]: !value } : d)
      );
      console.error('Error updating department setting:', error);
      showToast('error', t('Failed to update department setting'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ms-3 text-muted-foreground">{t('Loading settings...')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2
                        ${toast.type === 'success'
                          ? 'bg-success/10 text-success'
                          : 'bg-destructive/10 text-destructive'}`}>
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Persona Info Banner */}
      {selectedPersonaId && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            <p className="text-sm text-primary">
              {t('You are configuring settings for the selected persona. Changes will only apply to this persona.')}
            </p>
          </div>
        </div>
      )}

      {/* Company-Wide Policy */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Globe className="w-5 h-5 me-2" />
            {t('Company-Wide Policy')}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">{t('These settings apply to the entire company, not per-persona.')}</p>

          <div className="space-y-4">
            {/* Enable Public Profiles */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <Switch
                checked={companySettings?.publicProfilesEnabled ?? false}
                onCheckedChange={(checked) => handleCompanyToggle('publicProfilesEnabled', checked)}
                disabled={saving}
                label={t('Enable Public Profiles')}
                description={t('Allow employees to create public profiles')}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Persona-Specific Controls */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Users className="w-5 h-5 me-2" />
            {t('Persona Controls')}
            {personaSettings && (
              <span className="ms-2 text-sm font-normal text-muted-foreground">
                ({personaSettings.personaName})
              </span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">{t('These settings apply only to the selected persona.')}</p>

          <div className="space-y-4">
            {/* Allow Employee Toggle - Persona-specific */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-foreground">{t('Allow Employee Toggle')}</p>
                <p className="text-sm text-muted-foreground">{t('Employees with this persona can enable/disable their own profiles')}</p>
              </div>
              <input
                type="checkbox"
                checked={personaSettings?.allowEmployeeToggle ?? false}
                onChange={(e) => handlePersonaToggle('allowEmployeeToggle', e.target.checked)}
                disabled={saving || !personaSettings}
                className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
              />
            </div>

            {/* Requires Approval - Persona-specific */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-foreground">{t('Require Approval')}</p>
                <p className="text-sm text-muted-foreground">{t('Profiles with this persona require HR approval before publishing')}</p>
              </div>
              <input
                type="checkbox"
                checked={personaSettings?.requiresApproval ?? true}
                onChange={(e) => handlePersonaToggle('requiresApproval', e.target.checked)}
                disabled={saving || !personaSettings}
                className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
              />
            </div>

            {/* Allow Slug Regeneration - Persona-specific */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-foreground">{t('Allow Slug Regeneration')}</p>
                <p className="text-sm text-muted-foreground">{t('Employees can regenerate their profile URL slug')}</p>
              </div>
              <input
                type="checkbox"
                checked={personaSettings?.allowSlugRegeneration ?? true}
                onChange={(e) => handlePersonaToggle('allowSlugRegeneration', e.target.checked)}
                disabled={saving || !personaSettings}
                className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Department-Specific Restrictions */}
      <Card variant="elevated">
        <CardContent>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Building className="w-5 h-5 me-2" />
            {t('Department-Specific Settings')}
          </h2>

          {departmentSettings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('No departments found')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('Department')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('Profiles')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('Profiles Enabled')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('Require Approval')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {departmentSettings.map((dept) => (
                    <tr key={dept.departmentId} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-muted-foreground me-2" />
                          <span className="text-sm font-medium text-foreground">{dept.departmentName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {dept.profileCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={dept.profilesEnabled}
                          onChange={(e) => handleDepartmentToggle(dept.departmentId, 'profilesEnabled', e.target.checked)}
                          disabled={saving}
                          className="w-4 h-4 text-primary rounded focus-visible:ring-ring"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={dept.requiresApproval}
                          onChange={(e) => handleDepartmentToggle(dept.departmentId, 'requiresApproval', e.target.checked)}
                          disabled={saving || !dept.profilesEnabled}
                          className="w-4 h-4 text-primary rounded focus-visible:ring-ring disabled:opacity-30"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyControlsTab;
